import { Group, iconSizes, Stack, Tooltip } from '@common/ui'
import { IconInfoCircle } from '@common/ui/icons'
import React, { useEffect, useState } from 'react'
import { com } from '@eidu/web'
import FieldValueEqualsInput from './FieldValueEqualsInput'
import EntitySelectorTypeInput, { SupportedEntitySelectorType } from './EntitySelectorTypeInput'
import FieldValueEqualsUserFieldValueInput from './FieldValueEqualsUserFieldValueInput'
import FieldValueEqualsUserEntityId from './FieldValueEqualsUserEntityIdInput'
import SingleInput from './SingleInput'
import { findEntityTypeByFieldId } from '../../util/EntityTypeExtensions'
import EntityTypeField from '../../pages/permission/path/EntityTypeField'
import EntitySelector = com.eidu.entity.selector.EntitySelector
import EntityType = com.eidu.entity.type.EntityType
import EntityTypeId = com.eidu.entity.type.EntityTypeId

type EntitySelectorInputProps = {
  entityTypes: readonly EntityType[]
  entitySelector: EntitySelector
  setEntitySelector?: (selector: EntitySelector) => void
  disabled?: boolean
  lockedEntityTypeId?: EntityTypeId
  // When false, complex selectors are discontinued: a plain type hides the extra selector, while a legacy complex
  // selector stays visible (for backwards compatibility) but is flagged as no longer applying in new forms.
  complexSelectors?: boolean
}

const EntitySelectorInput: React.FC<EntitySelectorInputProps> = ({
  entityTypes,
  entitySelector,
  setEntitySelector,
  disabled,
  lockedEntityTypeId,
  complexSelectors = true,
}) => {
  const isDisabled = disabled || !setEntitySelector

  const [entitySelectorType, setEntitySelectorType] = useState<SupportedEntitySelectorType>()
  const [entityTypeId, setEntityTypeId] = useState<EntityTypeId | undefined>(lockedEntityTypeId)

  useEffect(() => {
    if (lockedEntityTypeId) {
      setEntityTypeId(lockedEntityTypeId)
      setEntitySelectorType(SupportedEntitySelectorType.All)
    } else if (entitySelector instanceof EntitySelector.FieldValueEqualsUserEntityId) {
      setEntitySelectorType(SupportedEntitySelectorType.FieldValueEqualsUserEntityId)
      const newEntityTypeId = findEntityTypeByFieldId(entityTypes, entitySelector.fieldId)
      if (newEntityTypeId) setEntityTypeId(newEntityTypeId.id)
    } else if (entitySelector instanceof EntitySelector.FieldValueEqualsUserFieldValue) {
      setEntitySelectorType(SupportedEntitySelectorType.FieldValueEqualsUserFieldValue)
      const newEntityTypeId = findEntityTypeByFieldId(entityTypes, entitySelector.fieldId)
      if (newEntityTypeId) setEntityTypeId(newEntityTypeId.id)
    } else if (entitySelector instanceof EntitySelector.Single) {
      setEntitySelectorType(SupportedEntitySelectorType.Single)
      setEntityTypeId(entitySelector.typeId)
    } else if (entitySelector instanceof EntitySelector.FieldValueEquals) {
      setEntitySelectorType(SupportedEntitySelectorType.FieldValueEquals)
      const newEntityTypeId = findEntityTypeByFieldId(entityTypes, entitySelector.fieldId)
      if (newEntityTypeId) setEntityTypeId(newEntityTypeId.id)
    } else if (entitySelector instanceof EntitySelector.OfType) {
      setEntitySelectorType(SupportedEntitySelectorType.All)
      setEntityTypeId(entitySelector.typeId)
    } else {
      setEntityTypeId(undefined)
      setEntitySelectorType(undefined)
    }
  }, [entitySelector, entityTypes, lockedEntityTypeId])

  return (
    <Stack gap="xxs" p={lockedEntityTypeId ? 4 : 8}>
      {entityTypes && !lockedEntityTypeId && (
        <EntityTypeField
          entityTypeId={entityTypeId}
          types={entityTypes}
          onChange={(newEntityType) => {
            setEntitySelector?.(new EntitySelector.OfType(newEntityType.id))
          }}
          label="Type"
          disabled={isDisabled}
        />
      )}
      {entityTypeId &&
        entitySelectorType !== undefined &&
        (complexSelectors || entitySelectorType !== SupportedEntitySelectorType.All) && (
          <Group gap="xs" align="center" wrap="nowrap">
            {!complexSelectors && (
              <Tooltip
                multiline
                maw={360}
                label="This selector type has been discontinued. It is kept for backwards compatibility but won't be applied in new forms — only the entity type is used. Select Filter → All to clear this message."
              >
                <IconInfoCircle
                  size={iconSizes.sm}
                  stroke={1.5}
                  color="var(--trellis-color-caution)"
                  data-testid="discontinued-selector-note"
                />
              </Tooltip>
            )}
            <EntitySelectorTypeInput
              optionsWithLabels={
                new Map([
                  [SupportedEntitySelectorType.All, 'All'],
                  [SupportedEntitySelectorType.Single, 'Specific entity'],
                  [SupportedEntitySelectorType.FieldValueEquals, 'Field value equals'],
                  [SupportedEntitySelectorType.FieldValueEqualsUserFieldValue, 'Field value equals user field value'],
                  [SupportedEntitySelectorType.FieldValueEqualsUserEntityId, 'Field value is user'],
                ])
              }
              selected={entitySelectorType}
              setSelected={(newType) => {
                if (newType === SupportedEntitySelectorType.All)
                  setEntitySelector?.(new EntitySelector.OfType(entityTypeId))
                else setEntitySelectorType(newType)
              }}
              disabled={isDisabled}
            />
            {entitySelectorType === SupportedEntitySelectorType.Single && (
              <SingleInput
                disabled={isDisabled}
                entityTypeId={entityTypeId}
                selector={entitySelector instanceof EntitySelector.Single ? entitySelector : undefined}
                setSelector={(newSelector) => setEntitySelector?.(newSelector)}
              />
            )}
            {entitySelectorType === SupportedEntitySelectorType.FieldValueEquals && (
              <FieldValueEqualsInput
                disabled={isDisabled}
                entityTypeId={entityTypeId}
                selector={entitySelector as EntitySelector.FieldValueEquals}
                setSelector={(newSelector) => {
                  if (newSelector) setEntitySelector?.(newSelector)
                }}
              />
            )}
            {entitySelectorType === SupportedEntitySelectorType.FieldValueEqualsUserFieldValue && (
              <FieldValueEqualsUserFieldValueInput
                entityTypeId={entityTypeId}
                allEntityTypes={entityTypes}
                selector={entitySelector as EntitySelector.FieldValueEqualsUserFieldValue}
                setSelector={(newSelector) => {
                  if (newSelector) setEntitySelector?.(newSelector)
                }}
                disabled={isDisabled}
              />
            )}
            {entitySelectorType === SupportedEntitySelectorType.FieldValueEqualsUserEntityId && (
              <FieldValueEqualsUserEntityId
                entityTypeId={entityTypeId}
                selector={entitySelector as EntitySelector.FieldValueEqualsUserEntityId}
                setSelector={(newSelector) => {
                  if (newSelector) setEntitySelector?.(newSelector)
                }}
                disabled={isDisabled}
              />
            )}
          </Group>
        )}
    </Stack>
  )
}

export default EntitySelectorInput
